import { Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import * as CryptoJS from 'crypto-js';
import { EncryptionSecretService } from './encryption-secret.service';
import { AuthService } from './auth.service';
import { Role } from '../_helpers/auth.constants';

@Injectable({
    providedIn: 'root'
})
export class PaymentGatewayProxyService {
    // Tracks the PAY_ID used in the most recent prepareRequest so processResponse
    // can reuse it for decryption (the encrypted PG response does not expose PAY_ID in plaintext).
    private lastPayId?: string;

    constructor(
        private readonly encryptionSecretService: EncryptionSecretService,
        private readonly authService: AuthService
    ) { }

    public isPaymentGatewayEndpoint(docId: string): boolean {
        return [
            'validate-phonenumber',
            'status-enquiry',
            'request-to-pay',
            'refund-initiation',
            'refund-status'
        ].includes(docId);
    }

    private hasEncryptedResponse(docId: string): boolean {
        return [
            'validate-phonenumber',
            'request-to-pay',
            'refund-initiation',
            'refund-status'
        ].includes(docId);
    }

    prepareRequest(
        docId: string,
        body: Record<string, unknown>
    ): Observable<Record<string, string>> {
        if (!this.isPaymentGatewayEndpoint(docId)) {
            return of(body as Record<string, string>);
        }

        const payId = this.requiredValue(body['PAY_ID'], 'PAY_ID');
        this.lastPayId = payId;

        return this.getMerchantCredentials(payId).pipe(
            map(credentials => this.buildGatewayPayload(
                docId,
                body,
                payId,
                credentials
            ))
        );
    }

    processResponse(
        docId: string,
        response: { status: number; statusText: string; body: any }
    ): Observable<{ status: number; statusText: string; body: any }> {
        if (!this.hasEncryptedResponse(docId)) {
            return of(response);
        }

        // The encrypted PG response does not expose PAY_ID in plaintext (it's inside ENCDATA),
        // so fall back to the PAY_ID captured during prepareRequest.
        const payId = this.extractPayIdFromResponse(response.body) ?? this.lastPayId;

        return this.getMerchantCredentials(payId).pipe(
            map(({ aesKey }) => ({
                ...response,
                body: this.parseDecryptedResponse(response.body, aesKey)
            }))
        );
    }


    private buildGatewayPayload(
        docId: string,
        body: Record<string, unknown>,
        payId: string,
        credentials: { aesKey: string; secretKey: string }
    ): Record<string, string> {
        switch (docId) {
            case 'validate-phonenumber':
                return this.buildEncryptedPayload(
                    this.prepareValidatePhoneNumber(body, payId),
                    payId,
                    credentials
                );

            case 'status-enquiry':
                return this.buildPlainSignedPayload(
                    this.prepareTransactionStatus(body, payId),
                    credentials.secretKey
                );

            case 'request-to-pay':
                return this.buildEncryptedPayload(
                    this.prepareCollectPay(body, payId),
                    payId,
                    credentials
                );


            case 'refund-initiation':
              return this.buildEncryptedPayload(
                this.prepareRefundInitiation(body, payId),
                payId,
                credentials
              );

            case 'refund-status':
              return this.buildPlainSignedPayload(
                this.prepareRefundStatus(body, payId),
                credentials.secretKey
              );

            default:
                throw new Error(`Unsupported payment gateway endpoint: ${docId}`);
        }
    }

    // ------------ Request body builder Methods ----------------


    private buildEncryptedPayload(
        params: Record<string, string>,
        payId: string,
        credentials: { aesKey: string; secretKey: string }
    ): Record<string, string> {
        const signedParams = this.addHash(params, credentials.secretKey);
        const plaintext = this.joinParams(signedParams);

        return {
            PAY_ID: payId,
            ENCDATA: this.encryptAes(plaintext, credentials.aesKey)
        };
    }

    private buildPlainSignedPayload(
        params: Record<string, string>,
        secretKey: string
    ): Record<string, string> {
        return this.addHash(params, secretKey);
    }

    private prepareValidatePhoneNumber(
        body: Record<string, unknown>,
        payId: string
    ): Record<string, string> {

        // 1. Prepare base parameters 
        const params: Record<string, string> = {
            PAY_ID: this.requiredValue(body['PAY_ID'], 'PAY_ID'),
            ORDER_ID: this.valueOrDefault(body['ORDER_ID'], `ORD${Date.now()}`),
            AMOUNT: this.requiredValue(body['AMOUNT'], 'AMOUNT'),
            CURRENCY_CODE: this.requiredValue(body['CURRENCY_CODE'], 'CURRENCY_CODE'),
            TXNTYPE: this.valueOrDefault(body['TXNTYPE'], 'SALE'),
            MOP_TYPE: this.valueOrDefault(body['MOP_TYPE'], 'PAYQR'),
            PAYMENT_TYPE: this.valueOrDefault(body['PAYMENT_TYPE'], 'PTQR'),
            CUST_EMAIL: this.requiredValue(body['CUST_EMAIL'], 'CUST_EMAIL'),
            CUST_PHONE: this.requiredValue(body['CUST_PHONE'], 'CUST_PHONE'),
            PAYMENTS_REGION: this.valueOrDefault(body['PAYMENTS_REGION'], 'DOMESTIC'),
            CARD_HOLDER_TYPE: this.valueOrDefault(body['CARD_HOLDER_TYPE'], 'CONSUMER')
        };

        const payerAddress = this.valueOrDefault(body['PAYER_ADDRESS'], '');
        if (payerAddress) {
            params['PAYER_ADDRESS'] = payerAddress;
        }

        return Object.fromEntries(
            Object.entries(params).sort(([leftKey], [rightKey]) =>
                leftKey.localeCompare(rightKey)
            )
        );
    }

    private prepareTransactionStatus(
        body: Record<string, unknown>,
        payId: string
    ): Record<string, string> {

        // 1. Prepare base parameters 
        const params: Record<string, string> = {
            PAY_ID: this.requiredValue(body['PAY_ID'], 'PAY_ID'),
            ORDER_ID: this.requiredValue(body['ORDER_ID'], 'ORDER_ID'),
            AMOUNT: this.requiredValue(body['AMOUNT'], 'AMOUNT'),
            CURRENCY_CODE: this.requiredValue(body['CURRENCY_CODE'], 'CURRENCY_CODE'),
            TXNTYPE: this.valueOrDefault(body['TXNTYPE'], 'STATUS')
        };

        return Object.fromEntries(
            Object.entries(params).sort(([leftKey], [rightKey]) =>
                leftKey.localeCompare(rightKey)
            )
        );
    }

    private prepareCollectPay(
        body: Record<string, unknown>,
        payId: string
    ): Record<string, string> {

        // 1. Prepare base parameters 
        const params: Record<string, string> = {
            PAY_ID: this.requiredValue(body['PAY_ID'], 'PAY_ID'),
            ORDER_ID: this.valueOrDefault(body['ORDER_ID'], `ORD${Date.now()}`),
            AMOUNT: this.requiredValue(body['AMOUNT'], 'AMOUNT'),
            CURRENCY_CODE: this.requiredValue(body['CURRENCY_CODE'], 'CURRENCY_CODE'),
            TXNTYPE: this.valueOrDefault(body['TXNTYPE'], 'SALE'),
            MOP_TYPE: this.valueOrDefault(body['MOP_TYPE'], 'PAYQR'),
            PAYMENT_TYPE: this.valueOrDefault(body['PAYMENT_TYPE'], 'PTQR'),
            CUST_EMAIL: this.requiredValue(body['CUST_EMAIL'], 'CUST_EMAIL'),
            CUST_PHONE: this.requiredValue(body['CUST_PHONE'], 'CUST_PHONE'),
            PAYMENTS_REGION: this.valueOrDefault(body['PAYMENTS_REGION'], 'DOMESTIC'),
            CARD_HOLDER_TYPE: this.valueOrDefault(body['CARD_HOLDER_TYPE'], 'CONSUMER')
        };

        // Optional fields — only include when present (presence of PAYER_ADDRESS flips QR ↔ RTP flow)

        const payerAddress = this.valueOrDefault(body['PAYER_ADDRESS'], '');
        const returnUrl = this.valueOrDefault(body['RETURN_URL'], '');
        const terminalId = this.valueOrDefault(body['TERMINAL_ID'], '');

        if (payerAddress) {
            params['PAYER_ADDRESS'] = payerAddress;
        }
        if (returnUrl) {
            params['RETURN_URL'] = returnUrl;
        }
        if (terminalId) {
            params['TERMINAL_ID'] = terminalId;
        }

        return Object.fromEntries(
            Object.entries(params).sort(([leftKey], [rightKey]) =>
                leftKey.localeCompare(rightKey)
            )
        );
    }

    private prepareRefundInitiation(
        body: Record<string, unknown>,
        payId: string
    ): Record<string, string> {

        // 1. Prepare base parameters 
        const params: Record<string, string> = {
            PAY_ID: this.requiredValue(body['PAY_ID'], 'PAY_ID'),
            ORDER_ID: this.requiredValue(body['ORDER_ID'], 'ORDER_ID'),
            AMOUNT: this.requiredValue(body['AMOUNT'], 'AMOUNT'),
            CURRENCY_CODE: this.requiredValue(body['CURRENCY_CODE'], 'CURRENCY_CODE'),
            TXNTYPE: this.valueOrDefault(body['TXNTYPE'], 'REFUND'),
            PG_REF_NUM: this.requiredValue(body['PG_REF_NUM'], 'PG_REF_NUM'),
            REFUND_FLAG: this.valueOrDefault(body['REFUND_FLAG'], 'R'),
            REFUND_ORDER_ID: this.requiredValue(body['REFUND_ORDER_ID'], 'REFUND_ORDER_ID')
        };

        return Object.fromEntries(
            Object.entries(params).sort(([leftKey], [rightKey]) =>
                leftKey.localeCompare(rightKey)
            )
        );
    }
    
    private prepareRefundStatus(
        body: Record<string, unknown>,
        payId: string
    ): Record<string, string> {

        // 1. Prepare base parameters 
        const params: Record<string, string> = {
            PAY_ID: this.requiredValue(body['PAY_ID'], 'PAY_ID'),
            ORDER_ID: this.requiredValue(body['ORDER_ID'], 'ORDER_ID'),
            REFUND_ORDER_ID: this.requiredValue(body['REFUND_ORDER_ID'], 'REFUND_ORDER_ID')
        };

        return Object.fromEntries(
            Object.entries(params).sort(([leftKey], [rightKey]) =>
                leftKey.localeCompare(rightKey)
            )
        );
    }


    //--------------------- Helper Methods ---------------------

    private valueOrDefault(value: unknown, defaultValue: string): string {
        const stringValue = String(value ?? '').trim();
        return stringValue || defaultValue;
    }

    private requiredValue(value: unknown, fieldName: string): string {
        const stringValue = String(value ?? '').trim();

        if (!stringValue) {
            throw new Error(`${fieldName} is required`);
        }

        return stringValue;
    }

    private getMerchantCredentials(payId?: string): Observable<{ aesKey: string; secretKey: string }> {
        // Only Pay10 Admin tests with different PAY_IDs, pass it to the encryption-key API
        // so the correct merchant's AES/salt keys are returned. Other roles use their own keys.
        const merchantId = this.authService.getRole() === Role.PAY10_ADMIN ? payId : undefined;
        return this.encryptionSecretService.loadEncryptionSecrets(merchantId).pipe(
            map(response => {
                const aesKey = response?.aesKey;
                const secretKey = response?.secretKey;

                if (!aesKey || !secretKey) {
                    throw new Error('Merchant AES key or secret key is missing');
                }

                return { aesKey, secretKey };
            })
        );
    }

    private extractPayIdFromResponse(responseBody: unknown): string | undefined {
        const obj = this.toResponseObject(responseBody);
        const payId = obj?.['PAY_ID'];
        const stringValue = payId == null ? '' : String(payId).trim();
        return stringValue || undefined;
    }

    private joinParams(params: Record<string, string>): string {
        return Object.entries(params)
            .sort(([leftKey], [rightKey]) => this.compareByCodeUnit(leftKey, rightKey))
            .map(([key, value]) => `${key}=${value}`)
            .join('~');
    }

    // Build sorted parameter map (similar to java TreeMap → deterministic hash)
    private compareByCodeUnit(left: string, right: string): number {
        if (left < right) {
            return -1;
        }
        if (left > right) {
            return 1;
        }
        return 0;
    }

    private addHash(params: Record<string, string>, secretKey: string): Record<string, string> {
        const joinedForHash = this.joinParams(params);
        const hash = CryptoJS.SHA256(joinedForHash + secretKey)
            .toString(CryptoJS.enc.Hex)
            .toUpperCase();

        return { ...params, HASH: hash };
    }

    private parseDecryptedResponse(responseBody: unknown, aesKey: string): unknown {
        const root = this.toResponseObject(responseBody);
        if (!root) {
            return responseBody;
        }

        const encData = root['ENCDATA'];
        if (typeof encData !== 'string' || !encData.trim()) {
            return Object.fromEntries(
                Object.entries(root).map(([key, value]) => [key, value == null ? '' : String(value)])
            );
        }

        return this.parseTildeParams(this.decryptAes(encData, aesKey));
    }

    private toResponseObject(responseBody: unknown): Record<string, unknown> | null {
        if (typeof responseBody === 'string') {
            try {
                responseBody = JSON.parse(responseBody);
            } catch {
                return null;
            }
        }

        if (!responseBody || typeof responseBody !== 'object' || Array.isArray(responseBody)) {
            return null;
        }

        return responseBody as Record<string, unknown>;
    }

    private parseTildeParams(data: string): Record<string, string> {
        const result: Record<string, string> = {};
        if (!data.trim()) {
            return result;
        }

        for (const pair of data.split('~')) {
            const index = pair.indexOf('=');
            if (index > 0) {
                result[pair.substring(0, index)] = pair.substring(index + 1);
            } else if (pair.trim()) {
                result[pair] = '';
            }
        }

        return result;
    }

    private decryptAes(encryptedData: string, aesKey: string): string {
        if (aesKey.length < 16) {
            throw new Error('AES key must contain at least 16 characters');
        }

        const key = CryptoJS.enc.Utf8.parse(aesKey);
        const iv = CryptoJS.enc.Utf8.parse(aesKey.substring(0, 16));
        const ciphertext = CryptoJS.lib.CipherParams.create({
            ciphertext: CryptoJS.enc.Base64.parse(encryptedData)
        });
        const decrypted = CryptoJS.AES.decrypt(ciphertext, key, {
            iv,
            mode: CryptoJS.mode.CBC,
            padding: CryptoJS.pad.Pkcs7
        }).toString(CryptoJS.enc.Utf8);

        if (!decrypted) {
            throw new Error('Failed to decrypt Pay10 response');
        }

        return decrypted;
    }

    private encryptAes(plaintext: string, aesKey: string): string {
        if (aesKey.length < 16) {
            throw new Error('AES key must contain at least 16 characters');
        }

        const key = CryptoJS.enc.Utf8.parse(aesKey);
        const iv = CryptoJS.enc.Utf8.parse(aesKey.substring(0, 16));
        const encrypted = CryptoJS.AES.encrypt(
            CryptoJS.enc.Utf8.parse(plaintext),
            key,
            {
                iv,
                mode: CryptoJS.mode.CBC,
                padding: CryptoJS.pad.Pkcs7
            }
        );

        return CryptoJS.enc.Base64.stringify(encrypted.ciphertext);
    }
}